import { LockingScript, P2PKH } from '@bsv/sdk'
import { getKeyInfo, signTransaction, type UnsignedTransactionTemplate } from './bsv-signing'

// The highest miner fee rate (sat/byte) the device accepts for a sweep. The
// server sizes the fee from the live rate, which is far below this.
export const STRAY_SWEEP_MAX_FEE_RATE = 1
const P2PKH_SWEEP_INPUT_BYTES = 148
const P2PKH_SWEEP_OUTPUT_BYTES = 34
const SWEEP_OVERHEAD_BYTES = 10

const p2pkhScriptHex = (address: string) => new P2PKH().lock(address).toHex()

/**
 * Refuses any sweep that does more than move coins sitting at this wallet's
 * ordinals address into this wallet's payment address: every input must be
 * locked to the ordinals address (and never a 1-sat ordinal), there must be
 * exactly one output paying the payment address, and the fee must be sane.
 * The server already left inscriptions out; this guards the device against a
 * template it did not ask for.
 */
export function validateStraySweepTemplate(
    template: UnsignedTransactionTemplate,
    addresses: { ordinalsAddress: string; paymentAddress: string },
): { inputSatoshis: number; outputSatoshis: number; fee: number } {
    const ordinalsScript = p2pkhScriptHex(addresses.ordinalsAddress)
    const paymentScript = p2pkhScriptHex(addresses.paymentAddress)
    const inputs = Array.isArray(template?.inputs) ? template.inputs : []
    const outputs = Array.isArray(template?.outputs) ? template.outputs : []
    if (!inputs.length) throw new Error('The move has nothing to spend')
    if ((template.version ?? 1) !== 1 || (template.lockTime ?? 0) !== 0)
        throw new Error('The move has an unexpected transaction format')

    const seen = new Set<string>()
    let inputSatoshis = 0
    for (const input of inputs) {
        const outpoint = `${input.txid}:${input.vout}`
        if (
            !/^[0-9a-f]{64}$/.test(input.txid) ||
            !Number.isSafeInteger(input.vout) ||
            input.vout < 0
        )
            throw new Error('The move spends an invalid output')
        if (seen.has(outpoint)) throw new Error('The move spends an output twice')
        seen.add(outpoint)
        if (LockingScript.fromHex(input.scriptPubKey).toHex() !== ordinalsScript)
            throw new Error('The move spends funds that are not at your ordinals address')
        if (!Number.isSafeInteger(input.satoshis) || input.satoshis <= 1)
            throw new Error('The move would spend an ordinal')
        inputSatoshis += input.satoshis
    }

    if (outputs.length !== 1) throw new Error('The move must pay only your balance')
    const [output] = outputs
    if (LockingScript.fromHex(output.script).toHex() !== paymentScript)
        throw new Error('The move must pay your own payment address')
    if (!Number.isSafeInteger(output.satoshis) || output.satoshis <= 0)
        throw new Error('The move pays an invalid amount')

    const fee = inputSatoshis - output.satoshis
    const maxFee =
        Math.ceil(
            (SWEEP_OVERHEAD_BYTES +
                inputs.length * P2PKH_SWEEP_INPUT_BYTES +
                P2PKH_SWEEP_OUTPUT_BYTES) *
                STRAY_SWEEP_MAX_FEE_RATE,
        ) + 1
    if (fee <= 0 || fee > maxFee) throw new Error('The move has an unexpected network fee')
    return { inputSatoshis, outputSatoshis: output.satoshis, fee }
}

/**
 * Signs a validated stray-funds sweep with the wallet's ordinals key. The
 * wallet's own keys decide both addresses, so a template for another wallet
 * is refused.
 */
export async function signStraySweep(
    template: UnsignedTransactionTemplate,
    wallet: { ordPk?: string; payPk?: string },
    expected?: { ordinalsAddress?: string; paymentAddress?: string },
): Promise<string> {
    const ordinals = wallet.ordPk ? getKeyInfo(wallet.ordPk) : null
    const payment = wallet.payPk ? getKeyInfo(wallet.payPk) : null
    if (!ordinals || !payment) throw new Error('Local wallet is missing its signing keys')
    if (
        (expected?.ordinalsAddress && expected.ordinalsAddress !== ordinals.address) ||
        (expected?.paymentAddress && expected.paymentAddress !== payment.address)
    )
        throw new Error('This move is for a different wallet')

    validateStraySweepTemplate(template, {
        ordinalsAddress: ordinals.address,
        paymentAddress: payment.address,
    })
    const signed = await signTransaction(template, wallet.ordPk!)
    if (!signed.success || !signed.rawTx) throw new Error(signed.error || 'Failed to sign the move')
    return signed.rawTx
}
